import { useDashboard } from "Dashboard.hook";
import { clsx } from "clsx";
import { ErrorWindow } from "components/ErrorWindow";
import { Layout } from "components/Layout";
import { LayoutSkeleton } from "components/LayoutSkeleton";
import { NewLevelDialog } from "components/NewLevelDialog";
import { Notification } from "components/Notification";
import { Login } from "components/Login";
import { SmallReferralButton } from "components/SmallReferralButton";
import { WarningWindow } from "components/WarningWindow";
import { ReactComponent as RSLogo } from "images/rs-logo.svg";
import * as FP from "utils/fp-js";
import "./Dashboard.css";
import { Container, EndIcon, Header } from "./Dashboard.style";
import { Spinner } from "./UI/Spinner";
import { ReferralButton } from "./components/ReferralButton";
import OneColModeIcon from "./images/one-col-mode-icon.svg";
import { Box, Tab, Tabs } from "@mui/material";
import { TabPanel } from "UI/TabPanel";
import { ButtonAdder } from "components/ButtonAdder";
import { NewButtonDialog } from "components/NewButtonDialog";
import { Onboarding } from "components/Onboarding";
import { Questions } from "components/Questions";
import { ReferralDetails } from "components/ReferralDetails";
import { Referrals } from "components/Referrals";
import { SenderAssignmentDialog } from "components/SenderAssignmentDialog";
import { useTabs } from "hooks/useTabs";
import { useState } from "react";
import TwoColsModeIcon from "./images/two-cols-mode-icon.svg";
import { SetPasswordDialog } from "components/SetPasswordDialog";

export const NUMBER_OF_LEVELS = Object.keys(EndIcon).length;
export const REFERRALS_IN_LEVEL = 50;

export const STARTER_MAX_PROGRESS = 20;
export const BRONZE_MAX_PROGRESS = 40;
export const SILVER_MAX_PROGRESS = 60;
export const GOLD_MAX_PROGRESS = 80;

export const mobileModes = {
  Profile: "Profile",
  Awards: "Awards",
  Dashboard: "Dashboard",
};

export const getUserLevel = (progress) =>
  FP.match(true)
    .on(progress >= 100, "Daimond")
    .on(progress >= GOLD_MAX_PROGRESS, "Platinum")
    .on(progress >= SILVER_MAX_PROGRESS, "Gold")
    .on(progress >= BRONZE_MAX_PROGRESS, "Silver")
    .on(progress >= STARTER_MAX_PROGRESS, "Bronze")
    .otherwise("Starter");

export const getUserPosition = (progress) =>
  FP.match(true)
    .on(progress >= 100, 5)
    .on(progress >= GOLD_MAX_PROGRESS, 10)
    .on(progress >= SILVER_MAX_PROGRESS, 20)
    .on(progress >= BRONZE_MAX_PROGRESS, 30)
    .on(progress >= STARTER_MAX_PROGRESS, 35)
    .otherwise(100);

export const getUserProgress = (referralsCount) =>
  (referralsCount * 100) / (REFERRALS_IN_LEVEL * NUMBER_OF_LEVELS);

export const Dashboard = () => {
  const [selectedReferralId, setSelectedReferralId] = useState(null);

  const {
    loading,
    setLoading,
    error,
    setError,
    notification,
    originator,
    buttonLocations,
    progress,
    levelProgress,
    newLevel,
    newCountsForUpdate,
    logos,
    modes,
    oneColMode,
    toggleColMode,
    showWarning,
    hideWarning,
    warning,
    handleBookmarkInfoClose,
    handleBookmarkInfoButtonClick,
    setNewProgress,
    userLevel,
    setNewLevel,
    setNewCountsForUpdate,
    setLevelProgress,
    isInstallNedeed,
    isButtonAdderOpen,
    setIsButtonAdderOpen,
    reloadButtons,
    isRecipientAdmin,
    hanldeLogout,
    senders,
    currentButton,
    setCurrentButton,
    updateSendersInButtonLocations,
    addNewSender,
    providers,
    addNewProvider,
    isPasswordCreateVisible,
    setIsPasswordCreateVisible,
  } = useDashboard();

  const selectNewlyAddedSender = (sender) => {
    addNewSender(sender);
    const senderId = sender?.id;
    if (!senderId) return;
    const currentButtonId = Object.keys(currentButton)[0];
    const newButtonLocations = buttonLocations.map((buttonLocation) => {
      const buttonLocationId = Object.keys(buttonLocation)[0];

      if (buttonLocationId === currentButtonId) {
        return {
          [buttonLocationId]: {
            ...buttonLocation[buttonLocationId],
            senders: [...buttonLocation[buttonLocationId].senders, sender],
          },
        };
      }
      return buttonLocation;
    });
    updateSendersInButtonLocations(newButtonLocations);
  };

  const { currentTab, handleChangeTab } = useTabs({
    key: "Dashboard",
    userData: originator,
  });

  if (!originator?.id && !loading && isInstallNedeed) {
    return (
      <Login
        error={error}
        setError={setError}
        setLoading={setLoading}
        notification={notification}
        originator={originator}
        handleBookmarkInfoButtonClick={handleBookmarkInfoButtonClick}
        handleBookmarkInfoClose={handleBookmarkInfoClose}
        reloadButtons={reloadButtons}
      />
    );
  }

  if (!originator?.id) {
    if (loading) return <LayoutSkeleton />;

    return (
      <Container>
        <Header>
          <RSLogo width={"103px"} />
        </Header>
        {!error && !loading && notification && (
          <WarningWindow
            title={<>Hello {originator?.firstName}!</>}
            content={notification}
            withCloseButton
            handleClose={handleBookmarkInfoClose}
            handleButtonClick={handleBookmarkInfoButtonClick}
          />
        )}
        {error && (
          <ErrorWindow error={error} handleClose={() => setError("")} />
        )}
      </Container>
    );
  }

  return (
    <Layout
      progress={progress}
      userLevel={userLevel}
      originator={originator}
      levelProgress={levelProgress}
      isRecipientAdmin={isRecipientAdmin}
      hanldeLogout={hanldeLogout}
      showError={setError}
      setSelectedReferralId={setSelectedReferralId}
      content={
        <>
          <Box
            sx={{
              marginBottom: 2,
              flex: 0,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Tabs
              value={currentTab}
              onChange={handleChangeTab}
              aria-label="tabs selector"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab label="Digital Buttons" />
              <Tab label="Questions" />
              <Tab label="Referrals" />
            </Tabs>
          </Box>
          <TabPanel value={currentTab} index={0}>
            <div className="absolute right-10 px-2 pb-4 text-xl font-bold flex flex-row justify-end items-center z-10">
              <img
                src={oneColMode ? TwoColsModeIcon : OneColModeIcon}
                alt="one row mode"
                className="cursor-pointer hover:opacity-80 block md:hidden"
                onClick={() => toggleColMode(!oneColMode)}
                title={oneColMode ? "Two columns mode" : "One column mode"}
              />
            </div>
            <div
              className={clsx(
                "hidden mt-2 md:flex md:flex-row md:flex-wrap gap-6 sm:gap-10 justify-center xl:justify-start items-start"
              )}
            >
              {buttonLocations?.map((buttonLocation, index) => (
                <ReferralButton
                  key={Object.keys(buttonLocation)[0]}
                  data={buttonLocation}
                  setError={setError}
                  logo={logos[index]}
                  mode={modes[index]}
                  setNewProgress={setNewProgress}
                  setNewLevel={setNewLevel}
                  setNewCountsForUpdate={setNewCountsForUpdate}
                  setLevelProgress={setLevelProgress}
                  originator={originator}
                  setCurrentButton={setCurrentButton}
                />
              ))}
              <ButtonAdder setIsButtonAdderOpen={setIsButtonAdderOpen} />
            </div>
            <div className="flex-1"></div>
            {oneColMode ? (
              <div
                className={
                  "mt-2 grid gap-6 grid-cols-1 place-self-center place-items-center md:hidden"
                }
              >
                {!buttonLocations?.length && (
                  <div className="text-xl w-full flex flex-col justify-center items-center text-gray-500">
                    No buttons to display
                  </div>
                )}
                {buttonLocations?.map((buttonLocation, index) => (
                  <ReferralButton
                    key={Object.keys(buttonLocation)[0]}
                    data={buttonLocation}
                    setError={setError}
                    logo={logos[index]}
                    mode={modes[index]}
                    setNewProgress={setNewProgress}
                    setNewLevel={setNewLevel}
                    setNewCountsForUpdate={setNewCountsForUpdate}
                    setLevelProgress={setLevelProgress}
                    originator={originator}
                    setCurrentButton={setCurrentButton}
                  />
                ))}
                <ButtonAdder
                  setIsButtonAdderOpen={setIsButtonAdderOpen}
                  oneColMode
                />
              </div>
            ) : (
              <div
                className={
                  "mt-4 grid gap-6 grid-cols-2 place-self-stsetIsButtonAdderOpenart place-items-center md:hidden"
                }
              >
                {!buttonLocations?.length && (
                  <div className="text-xl w-full flex flex-col justify-center items-center text-gray-500">
                    No buttons to display
                  </div>
                )}
                {buttonLocations?.map((buttonLocation, index) => (
                  <SmallReferralButton
                    key={Object.keys(buttonLocation)[0]}
                    data={buttonLocation}
                    setError={setError}
                    logo={logos[index]}
                    mode={modes[index]}
                    setNewProgress={setNewProgress}
                    setNewLevel={setNewLevel}
                    setNewCountsForUpdate={setNewCountsForUpdate}
                    setLevelProgress={setLevelProgress}
                    originator={originator}
                    setCurrentButton={setCurrentButton}
                  />
                ))}
                <ButtonAdder setIsButtonAdderOpen={setIsButtonAdderOpen} />
              </div>
            )}
          </TabPanel>
          <TabPanel value={currentTab} index={1}>
            <Questions
              originator={originator}
              setSelectedReferralId={setSelectedReferralId}
            />
          </TabPanel>
          <TabPanel value={currentTab} index={2}>
            <Referrals
              originator={originator}
              setSelectedReferralId={setSelectedReferralId}
            />
          </TabPanel>
        </>
      }
      dialogs={
        <>
          {!error && !loading && notification && (
            <WarningWindow
              title={<>Hello {originator?.firstName}!</>}
              content={notification}
              withCloseButton
              handleClose={handleBookmarkInfoClose}
              handleButtonClick={handleBookmarkInfoButtonClick}
            />
          )}
          {error && (
            <ErrorWindow error={error} handleClose={() => setError("")} />
          )}
          {loading && <Spinner />}
          {newLevel && (
            <NewLevelDialog
              level={newLevel}
              handleClose={() => setNewLevel("")}
              progress={progress}
              newCountsForUpdate={newCountsForUpdate}
              setNewProgress={setNewProgress}
            />
          )}
          <Notification warning={warning} hideWarning={hideWarning} />
          {isButtonAdderOpen && (
            <NewButtonDialog
              handleClose={() => setIsButtonAdderOpen(false)}
              setError={setError}
              showWarning={showWarning}
              originator={originator}
              reloadButtons={reloadButtons}
              providers={providers}
              addNewProvider={addNewProvider}
            />
          )}
          {!!currentButton && (
            <SenderAssignmentDialog
              handleClose={() => setCurrentButton(null)}
              senders={senders}
              currentButton={currentButton}
              originator={originator}
              setError={setError}
              updateSendersInButtonLocations={updateSendersInButtonLocations}
              selectNewlyAddedSender={selectNewlyAddedSender}
            />
          )}
          {!!selectedReferralId && (
            <ReferralDetails
              id={selectedReferralId}
              handleClose={() => setSelectedReferralId(null)}
              originator={originator}
              showError={setError}
            />
          )}
          {!notification && !isPasswordCreateVisible && (
            <Onboarding originator={originator} />
          )}
          {isPasswordCreateVisible && (
            <SetPasswordDialog
              dashboardAccessToken={originator.dashboardAccessToken}
              handleClose={() => setIsPasswordCreateVisible(false)}
              email={originator.email}
            />
          )}
        </>
      }
    />
  );
};
